import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import axios from '../lib/axios';

export interface Permission {
  sight_access: boolean;
  billing_access: boolean;
  integrations_access: boolean;
  scaleboard_access: boolean;
  support_access: boolean;
  isight_access: boolean;
  settings_access: boolean;
}

export const DEFAULT_PERMISSIONS: Permission = {
  sight_access: false,
  billing_access: false,
  integrations_access: false,
  scaleboard_access: false,
  support_access: false,
  isight_access: false,
  settings_access: false,
};

interface PermissionContextType {
  permissions: Permission;
  loading: boolean;
  currentMerchantId: number | null;
  updatePermissions: (merchantId: number, permissions: Permission) => void;
  checkPermission: (permission: keyof Permission) => boolean;
  checkPermissionForMerchant: (merchantId: number, permission: keyof Permission) => boolean;
  refreshPermissions: (merchantId: number) => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
  currentMerchantId?: number | null;
}

export function PermissionProvider({ children, currentMerchantId }: PermissionProviderProps) {
  const [permissions, setPermissions] = useState<Permission>(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(false);

  // Resolve initial merchant ID: prop > localStorage activeStoreId
  const resolveInitialMerchantId = (): number | null => {
    if (currentMerchantId) return currentMerchantId;
    const saved = localStorage.getItem('activeStoreId');
    return saved ? parseInt(saved, 10) : null;
  };

  const [merchantId, setMerchantId] = useState<number | null>(resolveInitialMerchantId);

  // Load permissions when merchant changes
  useEffect(() => {
    // Only load permissions if user is authenticated
    const token = localStorage.getItem('token');
    if (merchantId && token) {
      setLoading(true); // mark loading immediately so consumers don't act on stale permissions
      loadPermissions(merchantId);
    } else {
      setPermissions(DEFAULT_PERMISSIONS);
    }
  }, [merchantId]);

  // Sync with active store changes (store switch event or localStorage update)
  useEffect(() => {
    const handleStoreChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      // StoreSwitcher fires { storeId }, ActiveStoreContext fires the store object { id }
      const newStoreId: number | undefined = detail?.storeId ?? detail?.id;
      if (newStoreId && newStoreId !== merchantId) {
        setMerchantId(newStoreId);
      }
    };

    window.addEventListener('storeChanged', handleStoreChanged);
    return () => window.removeEventListener('storeChanged', handleStoreChanged);
  }, [merchantId]);

  const loadPermissions = async (merchantId: number) => {
    // Check authentication first
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, skipping permissions load');
      setPermissions(DEFAULT_PERMISSIONS);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`/permissions/stores/${merchantId}`);
      const data = response.data;
      
      // Debug: Log the actual response structure
      // console.log('Permission API Response:', data);
      
      if (data.success) {
        // Check if user is owner/admin for this store
        const isOwnerOrAdmin = data.user_role === 'owner' || data.user_role === 'admin';
        
        let parsedPermissions;
        
        if (isOwnerOrAdmin) {
          // Grant all permissions to owner/admin users
          parsedPermissions = {
            sight_access: true,
            billing_access: true,
            integrations_access: true,
            scaleboard_access: true,
            support_access: true,
            isight_access: true,
            settings_access: true,
          };
        } else {
          // Use stored permissions for regular users (backend returns object, not JSON string)
          const permissionData = data.permissions || {};
          
          // Debug: Log the permission data structure
          // console.log('Permission Data:', permissionData);
          // console.log('Permission Data Type:', typeof permissionData);
          // console.log('Permission Data Stringified:', JSON.stringify(permissionData));
          
          // Handle both object and string formats for backward compatibility
          let permissions = permissionData;
          if (typeof permissionData === 'string') {
            // console.log('Permission data is string, parsing...');
            try {
              permissions = JSON.parse(permissionData);
            } catch (e) {
                // console.error('Failed to parse permission string:', e);
                // console.error('String that failed to parse:', permissionData);
              permissions = {};
            }
          } else if (typeof permissionData === 'object' && permissionData !== null) {
            // console.log('Permission data is object, using directly');
            permissions = permissionData;
          } else {
            // console.log('Permission data is neither string nor object, using empty object');
            permissions = {};
          }
          
          parsedPermissions = {
            sight_access: permissions['sight_access'] ?? false,
            billing_access: permissions['billing_access'] ?? false,
            integrations_access: permissions['integrations_access'] ?? false,
            scaleboard_access: permissions['scaleboard_access'] ?? false,
            support_access: permissions['support_access'] ?? false,
            isight_access: permissions['isight_access'] ?? false,
            settings_access: permissions['settings_access'] ?? false,
          };
        }
        
        setPermissions(parsedPermissions);
      } else {
        // console.log('Failed to load permissions:', data.message || 'Unknown error');
        setPermissions(DEFAULT_PERMISSIONS);
      }
    } catch (error: any) {
      // console.error('Failed to load permissions:', error);
      
      // Check if this is a 403 error (access denied to store)
      if (error.response?.status === 403) {
        // console.log('Access denied to store - user may not have permission for this store');
        
        // Clear any stored store data that might be causing the issue
        localStorage.removeItem('activeStoreId');
        localStorage.removeItem('activeStoreShop');
        localStorage.removeItem('activeStore');
        localStorage.removeItem('shop');
        
        // Redirect to StoreHub where user can see their available stores/invitations
        if (window.location.pathname !== '/stores') {
          // console.log('Redirecting to StoreHub due to access denied');
          window.location.href = '/stores';
        }
      }
      
      setPermissions(DEFAULT_PERMISSIONS);
    } finally {
      setLoading(false);
    }
  };

  const updatePermissions = (merchantId: number, newPermissions: Permission) => {
    setPermissions(newPermissions);
    // Optionally persist to backend
    persistPermissions(merchantId, newPermissions);
  };

  const persistPermissions = async (merchantId: number, newPermissions: Permission) => {
    try {
      await axios.put(`/permissions/stores/${merchantId}`, newPermissions);
    } catch (error) {
      // console.error('Failed to persist permissions:', error);
    }
  };

  const checkPermission = (permission: keyof Permission): boolean => {
    return permissions[permission] || false;
  };

  const checkPermissionForMerchant = async (merchantId: number, permission: keyof Permission): Promise<boolean> => {
    try {
      const response = await axios.get(`/permissions/stores/${merchantId}/check/${permission}`);
      return response.data.has_permission || false;
    } catch (error) {
      console.error('Failed to check permission:', error);
      return false;
    }
  };

  const refreshPermissions = async (merchantId: number) => {
    await loadPermissions(merchantId);
  };

  const value: PermissionContextType = {
    permissions,
    loading,
    currentMerchantId: merchantId,
    updatePermissions,
    checkPermission,
    checkPermissionForMerchant,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions(): PermissionContextType {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

// Higher-order component for permission-based route protection
export function withPermissionCheck<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredPermission: keyof Permission,
  fallback?: React.ComponentType | null
) {
  return function PermissionProtectedComponent(props: P) {
    const { checkPermission } = usePermissions();
    const hasPermission = checkPermission(requiredPermission);

    if (!hasPermission) {
      if (fallback) {
        const FallbackComponent = fallback;
        return <FallbackComponent {...props} />;
      }
      return null;
    }

    return <WrappedComponent {...props} />;
  };
}

// Hook for conditional rendering based on permissions
export function usePermissionCondition(permission: keyof Permission) {
  const { checkPermission } = usePermissions();
  return checkPermission(permission);
}
